import React, { useEffect, useState, useRef } from "react";
import { http, API } from "../../lib/api";
import { useSettings } from "../../lib/SettingsContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Loader2, Upload, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";

// Extracted to avoid nested-component reconciliation issues.
function Section({ title, children }) {
  return (
    <div className="brutal-card p-6">
      <h3 className="font-display text-xl tracking-tight mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function AdminSettings() {
  const { settings: current, refresh } = useSettings();
  const [form, setForm] = useState(current);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingContact, setUploadingContact] = useState(false);
  const [products, setProducts] = useState([]);
  const logoInput = useRef(null);
  const contactImgInput = useRef(null);
  const contactImgInput2 = useRef(null);

  useEffect(() => { setForm(current); }, [current]);

  useEffect(() => {
    // Load active products for the "featured product" selector
    http.get("/admin/products").then((r) => setProducts(r.data || [])).catch((err) => { console.warn("Failed to load /admin/products:", err?.message || err); });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", `${API}/files/${data.storage_path}`);
      toast.success("Logo subido");
    } catch (e) {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  const uploadContactImage = async (file, slot = 1) => {
    if (!file) return;
    setUploadingContact(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(slot === 2 ? "contact_image_url_2" : "contact_image_url", data.storage_path);
      toast.success(`Imagen ${slot} subida`);
    } catch (e) {
      toast.error("Error al subir imagen de contacto");
    } finally {
      setUploadingContact(false);
    }
  };

  const addFeature = () => set("features", [...(form.features || []), { title: "", copy: "", icon: "star" }]);
  const updFeature = (i, k, v) => set("features", form.features.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const rmFeature = (i) => set("features", form.features.filter((_, idx) => idx !== i));

  const addSocial = () => set("social_links", [...(form.social_links || []), { label: "", url: "" }]);
  const updSocial = (i, k, v) => set("social_links", form.social_links.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const rmSocial = (i) => set("social_links", form.social_links.filter((_, idx) => idx !== i));

  // Homepage carousel slides — admin uploads image per slide
  const addSlide = () => set("homepage_carousel", [...(form.homepage_carousel || []), { image_url: "", title: "", subtitle: "", link: "" }]);
  const updSlide = (i, k, v) => set("homepage_carousel", (form.homepage_carousel || []).map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const rmSlide = (i) => set("homepage_carousel", (form.homepage_carousel || []).filter((_, idx) => idx !== i));

  // Homepage videos — admin adds YouTube / MP4 URLs
  const addVideo = () => set("homepage_videos", [...(form.homepage_videos || []), { url: "", title: "", subtitle: "", enabled: true }]);
  const updVideo = (i, k, v) => set("homepage_videos", (form.homepage_videos || []).map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const rmVideo = (i) => set("homepage_videos", (form.homepage_videos || []).filter((_, idx) => idx !== i));
  const moveSlide = (i, dir) => {
    const arr = [...(form.homepage_carousel || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("homepage_carousel", arr);
  };
  const [slideUploadIdx, setSlideUploadIdx] = useState(null);
  const uploadSlideImage = async (i, file) => {
    if (!file) return;
    setSlideUploadIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updSlide(i, "image_url", data.storage_path);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error("Error al subir imagen");
    } finally {
      setSlideUploadIdx(null);
    }
  };

  // Pre-armed offer examples — appended to current slides (customer edits image later)
  const loadCarouselExamples = () => {
    const nowMonth = new Date().toLocaleDateString("es-MX", { month: "long" });
    const examples = [
      { image_url: "", title: "Pack empresa 50 lanyards -20%", subtitle: "Oferta del mes", link: "/tienda?cat=lanyard" },
      { image_url: "", title: `Servicio de diseño gratis en Gafetes hasta fin de ${nowMonth}`, subtitle: "Promoción limitada", link: "/tienda?cat=gafete" },
      { image_url: "", title: "3x2 en playeras personalizadas", subtitle: "Verano", link: "/tienda?cat=playera" },
    ];
    set("homepage_carousel", [...(form.homepage_carousel || []), ...examples]);
    toast.success("Ejemplos agregados. Sube las imágenes y guarda.");
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/settings", form);
      await refresh();
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500">/admin/ajustes</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-1">Ajustes del sitio</h2>
        </div>
        <Button onClick={save} disabled={saving} className="brutal-btn bg-black hover:bg-[#FF3B30] text-white h-11 px-6" data-testid="settings-save">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Guardar cambios
        </Button>
      </div>

      <Section title="Marca">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Nombre de la marca</Label>
            <Input className="rounded-none mt-2" value={form.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} data-testid="settings-brand-name" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Color acento</Label>
            <div className="flex gap-2 mt-2">
              <input type="color" className="w-14 h-10 border border-neutral-200" value={form.brand_accent || "#FF3B30"} onChange={(e) => set("brand_accent", e.target.value)} />
              <Input className="rounded-none font-mono flex-1" value={form.brand_accent || ""} onChange={(e) => set("brand_accent", e.target.value)} />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              {form.logo_url ? (
                <div className="w-24 h-24 border border-neutral-200 p-2 bg-neutral-50 flex items-center justify-center">
                  <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-24 h-24 bg-black text-white flex items-center justify-center font-display text-3xl">
                  {form.brand_name?.[0] || "P"}
                </div>
              )}
              <div className="flex-1">
                <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="settings-logo-input" />
                <Button variant="outline" className="rounded-none border-black" onClick={() => logoInput.current?.click()} disabled={uploading} data-testid="settings-logo-upload">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Subir logo
                </Button>
                {form.logo_url && (
                  <Button variant="ghost" className="rounded-none ml-2 text-red-600" onClick={() => set("logo_url", null)}>
                    <Trash2 className="w-4 h-4 mr-1" /> Quitar
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">
              Tamaño del logo (altura): <span className="font-mono">{Number(form.logo_height) || 32}px</span>
            </Label>
            <div className="mt-2 flex items-center gap-4">
              <input
                type="range"
                min="24"
                max="96"
                step="2"
                value={Number(form.logo_height) || 32}
                onChange={(e) => set("logo_height", Number(e.target.value))}
                className="flex-1 accent-black"
                data-testid="settings-logo-height"
              />
              <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 flex items-center min-w-[120px] justify-center">
                {form.logo_url ? (
                  <img
                    src={form.logo_url}
                    alt="preview"
                    className="object-contain"
                    style={{ height: `${Number(form.logo_height) || 32}px`, maxWidth: "180px" }}
                  />
                ) : (
                  <span className="font-display tracking-tighter" style={{ fontSize: `${(Number(form.logo_height) || 32) * 0.7}px` }}>
                    {form.brand_name || "PRINTEC"}<span style={{ color: form.brand_accent || "#FF3B30" }}>.</span>
                  </span>
                )}
              </div>
            </div>
            <p className="text-[11px] text-neutral-500 mt-2">
              Ajusta la altura del logo que aparece en la barra superior y el pie de página (24–96px). El cambio se aplica en todas las páginas del sitio.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Hero (portada)">
        <div>
          <Label className="text-xs font-mono uppercase">Tagline superior</Label>
          <Input className="rounded-none mt-2" value={form.hero_tagline || ""} onChange={(e) => set("hero_tagline", e.target.value)} data-testid="settings-hero-tagline" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Línea 1</Label>
            <Input className="rounded-none mt-2" value={form.hero_line_1 || ""} onChange={(e) => set("hero_line_1", e.target.value)} data-testid="settings-hero-line-1" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Línea 2</Label>
            <Input className="rounded-none mt-2" value={form.hero_line_2 || ""} onChange={(e) => set("hero_line_2", e.target.value)} data-testid="settings-hero-line-2" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Línea 3 (acento)</Label>
            <Input className="rounded-none mt-2" value={form.hero_line_3 || ""} onChange={(e) => set("hero_line_3", e.target.value)} data-testid="settings-hero-line-3" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Subtítulo</Label>
          <Textarea rows={2} className="rounded-none mt-2" value={form.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} data-testid="settings-hero-subheadline" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Texto del botón principal</Label>
          <Input className="rounded-none mt-2" value={form.hero_cta_text || ""} onChange={(e) => set("hero_cta_text", e.target.value)} />
        </div>
      </Section>

      <Section title="Producto destacado en portada">
        <div>
          <Label className="text-xs font-mono uppercase">Producto que se mostrará en la sección destacada de la home</Label>
          <select
            className="rounded-none mt-2 w-full h-10 px-3 border border-neutral-200 bg-white text-sm"
            value={form.featured_product_id || ""}
            onChange={(e) => set("featured_product_id", e.target.value || null)}
            data-testid="settings-featured-product"
          >
            <option value="">— No mostrar producto destacado —</option>
            {products.filter((p) => p.active !== false).map((p) => (
              <option key={p.product_id} value={p.product_id}>
                {p.name} · ${Number(p.base_price).toFixed(2)}
              </option>
            ))}
          </select>
          <p className="text-xs text-neutral-500 mt-2">
            Selecciona qué producto se muestra en la sección destacada de la página principal. Si no seleccionas ninguno, no aparece esa sección.
          </p>
        </div>
      </Section>

      <Section title="Carrusel de portada (imágenes interactivas)">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="text-xs text-neutral-600 leading-snug max-w-2xl">
            Aparece en la portada como una fila de tarjetas verticales que rotan cada 5 segundos. Recomendado: <strong>al menos 6 slides</strong>, formato retrato (3:4). Enlaza a categorías (<span className="font-mono">/tienda?cat=lanyard</span>) o productos (<span className="font-mono">/producto/p_abc123</span>). Ideal para ofertas del mes.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none whitespace-nowrap" onClick={loadCarouselExamples} data-testid="settings-carousel-examples">
              Cargar ejemplos de ofertas
            </Button>
            <Button variant="outline" size="sm" className="rounded-none whitespace-nowrap" onClick={addSlide} data-testid="settings-carousel-add">
              <Plus className="w-3 h-3 mr-1" /> Agregar slide
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {(form.homepage_carousel || []).length === 0 && (
            <div className="text-xs text-neutral-500 italic border border-dashed border-neutral-300 p-4 text-center" data-testid="settings-carousel-empty">
              Aún no hay slides. Agrega al menos 6 para un carrusel completo.
            </div>
          )}
          {(form.homepage_carousel || []).map((slide, i) => (
            <div key={i} className="border border-neutral-200 p-3 grid grid-cols-12 gap-3 items-start" data-testid={`settings-carousel-slide-${i}`}>
              <div className="col-span-12 md:col-span-3">
                <div className="aspect-[3/4] max-w-[120px] bg-neutral-100 border border-neutral-200 overflow-hidden rounded-xl" data-testid={`settings-carousel-preview-${i}`}>
                  {slide.image_url ? (
                    <img
                      src={slide.image_url.startsWith("http") ? slide.image_url : `${API}/files/${slide.image_url}`}
                      alt={slide.title || `Slide ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] font-mono uppercase text-neutral-400">Sin imagen</div>
                  )}
                </div>
                <div className="mt-2 flex gap-1">
                  <label className="flex-1 border border-neutral-300 hover:border-black text-[11px] font-mono uppercase px-2 h-7 flex items-center justify-center cursor-pointer" data-testid={`settings-carousel-upload-${i}`}>
                    {slideUploadIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> {slide.image_url ? "Cambiar" : "Subir"}</>}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadSlideImage(i, e.target.files?.[0])}
                    />
                  </label>
                </div>
              </div>

              <div className="col-span-12 md:col-span-8 space-y-2">
                <Input
                  className="rounded-none h-9 text-sm"
                  placeholder="Título grande (ej. Playeras únicas)"
                  value={slide.title || ""}
                  onChange={(e) => updSlide(i, "title", e.target.value)}
                  data-testid={`settings-carousel-title-${i}`}
                />
                <Input
                  className="rounded-none h-9 text-sm"
                  placeholder="Subtítulo pequeño (ej. Colección Verano)"
                  value={slide.subtitle || ""}
                  onChange={(e) => updSlide(i, "subtitle", e.target.value)}
                />
                <Input
                  className="rounded-none h-9 text-sm font-mono"
                  placeholder="Link opcional (ej. /tienda?cat=playera)"
                  value={slide.link || ""}
                  onChange={(e) => updSlide(i, "link", e.target.value)}
                  data-testid={`settings-carousel-link-${i}`}
                />
              </div>

              <div className="col-span-12 md:col-span-1 flex md:flex-col items-center justify-end gap-1">
                <button type="button" onClick={() => moveSlide(i, -1)} disabled={i === 0} className="p-1 text-neutral-400 hover:text-black disabled:opacity-30" title="Subir" data-testid={`settings-carousel-up-${i}`}>▲</button>
                <button type="button" onClick={() => moveSlide(i, 1)} disabled={i === (form.homepage_carousel || []).length - 1} className="p-1 text-neutral-400 hover:text-black disabled:opacity-30" title="Bajar" data-testid={`settings-carousel-down-${i}`}>▼</button>
                <button type="button" onClick={() => rmSlide(i)} className="p-1 text-neutral-400 hover:text-red-600" title="Eliminar" data-testid={`settings-carousel-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Videos en la portada (YouTube / MP4)">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="text-xs text-neutral-600 leading-snug max-w-2xl">
            Pega un link de YouTube (<span className="font-mono">youtube.com/watch?v=...</span> o <span className="font-mono">youtu.be/...</span>) o una URL directa a <span className="font-mono">.mp4</span>. Se muestran como una cuadrícula en la portada, después de los beneficios. Puedes ocultar temporalmente sin borrar.
          </p>
          <Button variant="outline" size="sm" className="rounded-none whitespace-nowrap" onClick={addVideo} data-testid="settings-video-add">
            <Plus className="w-3 h-3 mr-1" /> Agregar video
          </Button>
        </div>
        <div className="space-y-2">
          {(form.homepage_videos || []).length === 0 && (
            <div className="text-xs text-neutral-500 italic border border-dashed border-neutral-300 p-4 text-center" data-testid="settings-video-empty">
              Aún no hay videos. Agrega el primero para mostrar la sección &quot;Video&quot; en la portada.
            </div>
          )}
          {(form.homepage_videos || []).map((v, i) => (
            <div key={i} className="border border-neutral-200 p-3 grid grid-cols-12 gap-3 items-start" data-testid={`settings-video-${i}`}>
              <div className="col-span-12 md:col-span-11 space-y-2">
                <Input
                  className="rounded-none h-9 text-sm font-mono"
                  placeholder="https://youtu.be/… o https://.../video.mp4"
                  value={v.url || ""}
                  onChange={(e) => updVideo(i, "url", e.target.value)}
                  data-testid={`settings-video-url-${i}`}
                />
                <Input
                  className="rounded-none h-9 text-sm"
                  placeholder="Título (opcional — se muestra debajo del video)"
                  value={v.title || ""}
                  onChange={(e) => updVideo(i, "title", e.target.value)}
                  data-testid={`settings-video-title-${i}`}
                />
                <Input
                  className="rounded-none h-9 text-sm"
                  placeholder="Subtítulo (opcional)"
                  value={v.subtitle || ""}
                  onChange={(e) => updVideo(i, "subtitle", e.target.value)}
                />
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={v.enabled !== false}
                    onChange={(e) => updVideo(i, "enabled", e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid={`settings-video-enabled-${i}`}
                  />
                  <span className={v.enabled !== false ? "text-emerald-700 font-medium" : "text-neutral-400"}>
                    {v.enabled !== false ? "Visible en la portada" : "Oculto"}
                  </span>
                </label>
              </div>
              <div className="col-span-12 md:col-span-1 flex md:flex-col items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => rmVideo(i)}
                  className="p-1 text-neutral-400 hover:text-red-600"
                  title="Eliminar"
                  data-testid={`settings-video-remove-${i}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Beneficios (4 tarjetas debajo del hero)">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="rounded-none" onClick={addFeature}><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
        </div>
        <div className="space-y-2">
          {(form.features || []).map((f, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start border border-neutral-200 p-2">
              <Input placeholder="Icono (palette|package|truck|shield|star)" className="col-span-2 rounded-none h-9 text-sm font-mono" value={f.icon || ""} onChange={(e) => updFeature(i, "icon", e.target.value)} />
              <Input placeholder="Título" className="col-span-3 rounded-none h-9 text-sm" value={f.title || ""} onChange={(e) => updFeature(i, "title", e.target.value)} />
              <Input placeholder="Descripción" className="col-span-6 rounded-none h-9 text-sm" value={f.copy || ""} onChange={(e) => updFeature(i, "copy", e.target.value)} />
              <button onClick={() => rmFeature(i)} className="p-2 hover:bg-neutral-100"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Datos de la empresa (aparecen en Contacto y footer)">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Nombre de la empresa</Label>
            <Input className="rounded-none mt-2" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="settings-company-name" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Correo</Label>
            <Input className="rounded-none mt-2" value={form.company_email || ""} onChange={(e) => set("company_email", e.target.value)} data-testid="settings-company-email" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Teléfono</Label>
            <Input className="rounded-none mt-2" value={form.company_phone || ""} onChange={(e) => set("company_phone", e.target.value)} data-testid="settings-company-phone" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">WhatsApp</Label>
            <Input className="rounded-none mt-2" value={form.company_whatsapp || ""} onChange={(e) => set("company_whatsapp", e.target.value)} data-testid="settings-company-whatsapp" placeholder="+52 55 1234 5678" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Dirección</Label>
            <Input className="rounded-none mt-2" value={form.company_address || ""} onChange={(e) => set("company_address", e.target.value)} data-testid="settings-company-address" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Ciudad</Label>
            <Input className="rounded-none mt-2" value={form.company_city || ""} onChange={(e) => set("company_city", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Horarios</Label>
            <Input className="rounded-none mt-2" value={form.company_hours || ""} onChange={(e) => set("company_hours", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Link de reseña de Google</Label>
            <Input
              className="rounded-none mt-2 font-mono text-xs"
              placeholder="https://g.page/r/..."
              value={form.google_review_url || ""}
              onChange={(e) => set("google_review_url", e.target.value)}
              data-testid="settings-google-review-url"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Se envía al cliente por WhatsApp desde Pedidos → “Solicitar reseña de Google”. Obtén el link desde tu ficha de Google Business Profile (Compartir reseña).</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Dirección completa (multilínea)</Label>
            <Textarea
              rows={3}
              className="rounded-none mt-2"
              placeholder="Calle Álvaro Obregón 123&#10;Col. Centro, C.P. 64000&#10;Monterrey, Nuevo León&#10;Referencias: frente a la plaza"
              value={form.company_address_full || ""}
              onChange={(e) => set("company_address_full", e.target.value)}
              data-testid="settings-company-address-full"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Se muestra en la página <span className="font-mono">/contacto</span> con enlace a Google Maps. Si la dejas vacía se compone de los campos "Dirección" y "Ciudad" de arriba.</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Imágenes del negocio (aparecen en /contacto)</Label>
            <p className="text-[11px] text-neutral-500 mt-1 mb-2">Puedes subir hasta 2 fotos (local, taller, equipo, vitrina). Recomendado: 1200×900 px, JPG o PNG.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Slot 1 */}
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500 mb-2">Imagen 1</div>
                <div className="flex items-start gap-3">
                  <div className="w-24 h-24 border border-neutral-200 bg-neutral-50 overflow-hidden flex-shrink-0" data-testid="settings-contact-image-preview">
                    {form.contact_image_url ? (
                      <img
                        src={form.contact_image_url.startsWith("http") ? form.contact_image_url : `${API}/files/${form.contact_image_url}`}
                        alt="Contacto 1"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] font-mono uppercase text-neutral-400">Sin imagen</div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={contactImgInput}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadContactImage(e.target.files?.[0], 1)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none h-9 w-full"
                      onClick={() => contactImgInput.current?.click()}
                      disabled={uploadingContact}
                      data-testid="settings-contact-image-upload"
                    >
                      {uploadingContact ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {form.contact_image_url ? "Cambiar" : "Subir"}
                    </Button>
                    {form.contact_image_url && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none h-8 w-full border-red-300 text-red-600 text-xs"
                        onClick={() => set("contact_image_url", null)}
                        data-testid="settings-contact-image-remove"
                      >
                        <Trash2 className="w-3 h-3 mr-1.5" /> Quitar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {/* Slot 2 */}
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500 mb-2">Imagen 2</div>
                <div className="flex items-start gap-3">
                  <div className="w-24 h-24 border border-neutral-200 bg-neutral-50 overflow-hidden flex-shrink-0" data-testid="settings-contact-image-2-preview">
                    {form.contact_image_url_2 ? (
                      <img
                        src={form.contact_image_url_2.startsWith("http") ? form.contact_image_url_2 : `${API}/files/${form.contact_image_url_2}`}
                        alt="Contacto 2"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] font-mono uppercase text-neutral-400">Sin imagen</div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={contactImgInput2}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadContactImage(e.target.files?.[0], 2)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none h-9 w-full"
                      onClick={() => contactImgInput2.current?.click()}
                      disabled={uploadingContact}
                      data-testid="settings-contact-image-2-upload"
                    >
                      {uploadingContact ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {form.contact_image_url_2 ? "Cambiar" : "Subir"}
                    </Button>
                    {form.contact_image_url_2 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none h-8 w-full border-red-300 text-red-600 text-xs"
                        onClick={() => set("contact_image_url_2", null)}
                        data-testid="settings-contact-image-2-remove"
                      >
                        <Trash2 className="w-3 h-3 mr-1.5" /> Quitar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Datos bancarios (transferencia)">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Banco</Label>
            <Input className="rounded-none mt-2" value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} data-testid="settings-bank-name" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Beneficiario</Label>
            <Input className="rounded-none mt-2" value={form.bank_beneficiary || ""} onChange={(e) => set("bank_beneficiary", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">CLABE / cuenta</Label>
            <Input className="rounded-none mt-2 font-mono" value={form.bank_clabe || ""} onChange={(e) => set("bank_clabe", e.target.value)} data-testid="settings-bank-clabe" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Email para comprobantes</Label>
            <Input className="rounded-none mt-2" value={form.bank_email || ""} onChange={(e) => set("bank_email", e.target.value)} />
          </div>
        </div>
      </Section>

      <Section title="Costos de envío (entrega a domicilio)">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase">Costo de envío a domicilio (MXN)</Label>
            <Input
              type="number"
              step="1"
              min="0"
              className="rounded-none mt-2"
              value={form.shipping_fee_domicilio ?? 99}
              onChange={(e) => set("shipping_fee_domicilio", Number(e.target.value))}
              data-testid="settings-shipping-fee"
            />
            <p className="text-xs text-neutral-500 mt-1">Este es el costo estándar para envío a domicilio. Recolección en sucursal siempre es sin costo.</p>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Envío gratis a partir de (MXN)</Label>
            <Input
              type="number"
              step="10"
              min="0"
              className="rounded-none mt-2"
              value={form.shipping_free_threshold ?? 0}
              onChange={(e) => set("shipping_free_threshold", Number(e.target.value))}
              data-testid="settings-shipping-threshold"
            />
            <p className="text-xs text-neutral-500 mt-1">Deja en <span className="font-mono">0</span> para desactivar el envío gratis por monto mínimo.</p>
          </div>
        </div>
      </Section>

      <Section title="Redes sociales">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="rounded-none" onClick={addSocial}><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
        </div>
        <div className="space-y-2">
          {(form.social_links || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center border border-neutral-200 p-2">
              <Input placeholder="Etiqueta (Instagram, TikTok...)" className="col-span-4 rounded-none h-9 text-sm" value={s.label || ""} onChange={(e) => updSocial(i, "label", e.target.value)} />
              <Input placeholder="URL" className="col-span-7 rounded-none h-9 text-sm font-mono" value={s.url || ""} onChange={(e) => updSocial(i, "url", e.target.value)} />
              <button onClick={() => rmSocial(i)} className="p-2 hover:bg-neutral-100"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="brutal-btn bg-black hover:bg-[#FF3B30] text-white h-11 px-6">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
